import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId, CanvasElementType } from '@blaze-runtime/engine-domain';
import {
  ElementDefinitionsService,
  ElementStylesService,
  fromElements,
  getStyleReferences,
  isNavigationContainerType,
} from '@blaze-runtime/engine-feature';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import { compareAscBy } from '@blaze-shared/platform-util';
import { isNavigationPageType } from '@blaze-shared/projects-domain';
import { isDefined } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { themesSelectors } from '@blaze-shared/theming-data-access';
import {
  AppTheme,
  BLOCKED_DEFAULT_STYLE_ELEMENT_TYPES,
  StyleSheet,
} from '@blaze-shared/theming-domain';

interface ReferenceCategory {
  name: string;
  canSetDefaultStyle: boolean;
  styleRefs: StyleReference[];
}

interface StyleReference {
  styleSheet: StyleSheet;
  icon: string;
  elementRefsCount: number;
  currentPageElementRefs: ElementReference[];
  otherPagesElementRefs: ElementReference[];
}

interface ElementReference {
  elementId: CanvasElementId;
  name: string;
}

@UntilDestroy()
@Component({
  selector: 'blaze-style-sheets-panel',
  templateUrl: './style-sheets-panel.component.html',
  styleUrls: ['./style-sheets-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StyleSheetsPanelComponent implements OnInit {
  private readonly styleSheetsFacade = inject(fromStyles.StyleSheetsFacade);
  private readonly elementsFacade = inject(fromElements.ElementsFacade);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly store = inject(Store);

  @Input() projectId!: string;

  expandedStyleSheetId: string | undefined;
  referenceCategories$: Observable<ReferenceCategory[]>;
  selectedElement$ = this.elementsFacade.getSelected();
  updating$ = this.styleSheetsFacade.updatePending$;
  sheets$ = this.elementStylesService.sheets$;
  selectedTheme: AppTheme | undefined;
  constructor() {
    this.store
      .select(themesSelectors.getSelectedTheme)
      .pipe(untilDestroyed(this))
      .subscribe((theme) => {
        this.selectedTheme = theme;
      });
    this.referenceCategories$ = combineLatest({
      styleSheets: this.elementStylesService.sheets$,
      currentPageId: this.store.select(fromRouter.getPageId),
      pages: this.store.select(runtimeSelectors.selectPages),
      elements: this.elementsFacade.entities$,
    }).pipe(
      map(({ styleSheets, currentPageId, pages, elements }) => {
        const refsCategoryMap = new Map<string, ReferenceCategory>();

        const currentPageElements = Object.values(elements).filter(isDefined);
        const currentPageReferences = getStyleReferences(currentPageElements, styleSheets) ?? [];

        for (const styleSheet of styleSheets) {
          const definition = this.elementDefinitionsService.getDefinition(
            styleSheet.elementType as CanvasElementType,
          );

          const otherPagesElementRefs: ElementReference[] = [];

          const currentStyleReference = currentPageReferences.find((reference) => {
            // References that do not have elementType are most likely to be global.
            if (reference.elementType && reference.elementType !== styleSheet.elementType) {
              return false;
            }

            return (
              (reference.styleId === styleSheet.id && !styleSheet.themeId) ||
              (reference.styleName === styleSheet.name && !!styleSheet.themeId)
            );
          });

          const currentPageElementRefs = (currentStyleReference?.elementIds ?? []).map(
            (elementId: string) => {
              const element = elements[elementId];
              return {
                elementId: elementId,
                name: element?.name ?? definition?.name ?? '',
              };
            },
          );

          // The only other page that a navigation container is going to be is the page that it hosts.
          // Prevent showing this reference allowing user to visit this header page.
          if (!isNavigationContainerType(definition?.type)) {
            for (const reference of styleSheet.references) {
              if (reference.pageId !== currentPageId) {
                const page = pages.find((page) => page.id === reference.pageId);
                const isNavigationPage = isNavigationPageType(page?.pageType);

                for (const elementId of reference.elementIds) {
                  const isPresentInCurrentPage = currentPageElementRefs.some(
                    (ref) => ref.elementId === elementId,
                  );

                  if (isNavigationPage && isPresentInCurrentPage) {
                    continue;
                  }

                  otherPagesElementRefs.push({
                    elementId,
                    name: `${definition?.name ?? ''} ${page ? `(${page.name})` : ''}`,
                  });
                }
              }
            }
          }

          const styleRef: StyleReference = {
            styleSheet,
            icon: `ei-${styleSheet.elementType}`,
            elementRefsCount: currentPageElementRefs.length + otherPagesElementRefs.length,
            currentPageElementRefs,
            otherPagesElementRefs,
          };

          const refCategory = refsCategoryMap.get(styleSheet.elementType);
          if (refCategory) {
            refCategory.styleRefs.push(styleRef);
          } else {
            if (definition) {
              refsCategoryMap.set(styleSheet.elementType, {
                name: definition?.name ?? 'No name found',
                canSetDefaultStyle: !BLOCKED_DEFAULT_STYLE_ELEMENT_TYPES.includes(
                  styleSheet.elementType,
                ),
                styleRefs: [styleRef],
              });
            }
          }
        }
        const refsCategories: ReferenceCategory[] = [];
        for (const refCategory of refsCategoryMap.values()) {
          refsCategories.push(refCategory);
        }

        return refsCategories.toSorted(compareAscBy('name'));
      }),
    );
  }

  ngOnInit(): void {
    this.styleSheetsFacade.loadStyles(this.projectId, true);
  }

  toggleExpand(styleRef: StyleReference): void {
    this.expandedStyleSheetId =
      this.expandedStyleSheetId !== styleRef.styleSheet.id ? styleRef.styleSheet.id : undefined;
  }

  isExpanded(styleRef: StyleReference): boolean {
    return this.expandedStyleSheetId === styleRef.styleSheet.id && !!styleRef.elementRefsCount;
  }

  isDefault(styleRef: StyleReference): boolean {
    return styleRef.styleSheet.isThemeDefault;
  }

  isSelected(
    elementRef: ElementReference,
    selected: fromElements.SelectedPathData | undefined,
  ): boolean {
    return elementRef.elementId === selected?.element?.id;
  }

  delete(styleRef: StyleReference): void {
    this.styleSheetsFacade.delete(styleRef.styleSheet.id);
  }

  setDefault(styleRef: StyleReference): void {
    this.styleSheetsFacade.setDefaultInTheme(styleRef.styleSheet.id);
  }

  navigateToElement(elementRef: ElementReference): void {
    this.elementsFacade.navigateToNode(elementRef.elementId);
  }
}
